/**
 * System-cluster panels (PXL): Settings, Accessibility, and Developer Debug Tools.
 * Settings/Accessibility are client-local (localStorage + CSS variables on :root).
 * Debug (DEV BUILDS ONLY — panels/index.tsx registers it behind devToolsEnabled()) surfaces the live connection, a store inspector, the event/combat logs and
 * a raw-intent sender so every wire message is exercisable during testing.
 */

import { useEffect, useState } from 'react';

import type { ClientMessage } from '@great-cloud/shared/client';

import { CAMERA_PRESETS, CAPTURE_KEYS } from '../../game/capture.js';
import { getAudioSettings, setBusSetting, subscribeAudioSettings } from '../../game/audio/index.js';
import { AUDIO_BUSES } from '../../game/audio/settings.js';
import type { AudioBus, AudioSettings } from '../../game/audio/settings.js';
import { VFX_LEVELS, setVfxLevel, vfxLevel, type VfxLevel } from '../../game/vfx/settings.js';
import { useGuestMode } from '../../guest/guestContext.js';
import { buildSha, shortSha } from '../../version.js';
import { BugReportForm } from '../BugReport.js';
import { applyA11yPrefs } from '../a11yPrefs.js';
import { ORIENTATION_PREFS, parseOrientationPref } from '../layout/layoutMode.js';
import type { OrientationPref } from '../layout/layoutMode.js';
import { setLayoutPrefs } from '../layout/layoutPrefs.js';
import { applyNativeOrientationLock, browserLockPorts } from '../layout/orientationLock.js';
import { useLayoutPrefs } from '../layout/useLayout.js';
import { useGame } from '../net-context.js';
import { DataView, Empty, SectionTitle, Stat, Tabs } from '../widgets.js';
import { keybindRows, type PanelKeyDef } from './keybinds.js';

function usePersistentNumber(key: string, fallback: number): [number, (v: number) => void] {
  const [v, setV] = useState<number>(() => {
    const raw = localStorage.getItem(key);
    return raw ? Number(raw) : fallback;
  });
  const set = (n: number): void => {
    setV(n);
    localStorage.setItem(key, String(n));
  };
  return [v, set];
}

function usePersistentFlag(key: string): [boolean, (v: boolean) => void] {
  const [v, setV] = useState<boolean>(() => localStorage.getItem(key) === '1');
  const set = (b: boolean): void => {
    setV(b);
    localStorage.setItem(key, b ? '1' : '0');
  };
  return [v, set];
}

function openCookieSettings(): void {
  const cc = (window as { CookieConsent?: { showPreferences?: () => void } }).CookieConsent;
  cc?.showPreferences?.();
}

/** `panels` is the registry (`panels/index.tsx` passes it), so the Keybinds list is derived. */
export function SettingsPanel({ panels }: { panels: readonly PanelKeyDef[] }): JSX.Element {
  const guest = useGuestMode();
  const [scale, setScale] = usePersistentNumber('pxl.fontScale', 13);
  const [showMinimap, setShowMinimap] = usePersistentFlag('pxl.minimap');
  const [vfx, setVfx] = useState<VfxLevel>(() => vfxLevel());
  // E1.5 capture presentation prefs (discoverable without the hotkeys). Hotkeys
  // (F4/F3/`[`/`]`) are the live in-scene toggle; these persist the same keys and
  // take effect on the next capture toggle / reload. Camera presets stay within
  // the camera bounds by construction (CAMERA_PRESETS is bounds-checked in tests).
  const [captureMode, setCaptureMode] = usePersistentFlag(CAPTURE_KEYS.captureMode);
  const [hudHidden, setHudHidden] = usePersistentFlag(CAPTURE_KEYS.hudHidden);
  const [hideHealthBars, setHideHealthBars] = useState<boolean>(
    () => localStorage.getItem(CAPTURE_KEYS.captureHideHealthBars) !== '0', // default ON
  );
  const [slowOrbit, setSlowOrbit] = usePersistentFlag(CAPTURE_KEYS.slowOrbit);
  const [preset, setPreset] = useState<string>(
    () => localStorage.getItem(CAPTURE_KEYS.capturePreset) ?? CAMERA_PRESETS[0].id,
  );
  useEffect(() => {
    document.documentElement.style.setProperty('--pxl-font', `${scale}px`);
  }, [scale]);
  return (
    <div className="pxl-list">
      <DisplaySettings />
      <SoundSettings />
      <SectionTitle>Interface</SectionTitle>
      <label className="pxl-stat">
        <span className="k">UI text size ({scale}px)</span>
        <input
          type="range"
          min={11}
          max={18}
          value={scale}
          onChange={(e) => setScale(Number(e.target.value))}
        />
      </label>
      <label className="pxl-stat">
        <span className="k">Hide minimap</span>
        <input
          type="checkbox"
          checked={showMinimap}
          onChange={(e) => setShowMinimap(e.target.checked)}
        />
      </label>
      <label className="pxl-stat">
        <span className="k">Visual effects</span>
        <select
          className="pxl-select"
          value={vfx}
          onChange={(e) => {
            const level = e.target.value as VfxLevel;
            setVfxLevel(level);
            setVfx(level);
          }}
        >
          {VFX_LEVELS.map((l) => (
            <option key={l} value={l}>
              {l === 'full' ? 'Full' : l === 'reduced' ? 'Reduced' : 'Off'}
            </option>
          ))}
        </select>
      </label>
      <SectionTitle>Capture (F4 · F3 · [ ])</SectionTitle>
      <label className="pxl-stat">
        <span className="k">Capture mode (hide UI) — F4</span>
        <input
          type="checkbox"
          checked={captureMode}
          onChange={(e) => setCaptureMode(e.target.checked)}
        />
      </label>
      <label className="pxl-stat">
        <span className="k">Hide HUD — F3</span>
        <input
          type="checkbox"
          checked={hudHidden}
          onChange={(e) => setHudHidden(e.target.checked)}
        />
      </label>
      <label className="pxl-stat">
        <span className="k">Hide health bars in capture</span>
        <input
          type="checkbox"
          checked={hideHealthBars}
          onChange={(e) => {
            setHideHealthBars(e.target.checked);
            localStorage.setItem(CAPTURE_KEYS.captureHideHealthBars, e.target.checked ? '1' : '0');
          }}
        />
      </label>
      <label className="pxl-stat">
        <span className="k">Slow-orbit drift</span>
        <input
          type="checkbox"
          checked={slowOrbit}
          onChange={(e) => setSlowOrbit(e.target.checked)}
        />
      </label>
      <label className="pxl-stat">
        <span className="k">Camera preset</span>
        <select
          className="pxl-select"
          value={preset}
          onChange={(e) => {
            setPreset(e.target.value);
            localStorage.setItem(CAPTURE_KEYS.capturePreset, e.target.value);
          }}
        >
          {CAMERA_PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <SectionTitle>Keybinds</SectionTitle>
      <div className="pxl-grid2">
        {keybindRows(panels, guest !== null).map(([k, v]) => (
          <Stat key={k} k={k} v={v} />
        ))}
      </div>
      <div className="pxl-faint">
        Graphics/audio settings are placeholders for gameplay testing.
      </div>
      <SectionTitle>Privacy</SectionTitle>
      {/* AD-641 — reopen the self-hosted consent banner (index.html loads it). Absent when
          the library was blocked, in which case analytics never loaded either. */}
      <button type="button" className="pxl-btn" onClick={openCookieSettings}>
        Cookie settings
      </button>
      {/* q-bug-report-button: one click files a report; the server attaches the context. Not in
          guest mode: the guest process keeps no database, so a report would be lost (walled). */}
      {guest === null ? (
        <>
          <SectionTitle>Support</SectionTitle>
          <BugReportForm />
        </>
      ) : null}
      <SectionTitle>About</SectionTitle>
      {/* E1.6.S1 — the in-client build/version stamp (also logged to the console at
          boot). Short sha shown; hover for the full sha. Client-build-local, never
          on the wire. 'unstamped' when built from a .git-less copy with no override. */}
      <Stat
        k="Build"
        v={
          <span title={buildSha()} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {shortSha()}
          </span>
        }
      />
    </div>
  );
}

/**
 * The stick + touch bar overlay is built (T3, `q-touch-controls-overlay`; the HUD renders
 * `ui/touch/TouchControlsOverlay` when `useMobileControlsEnabled()`), so the setting no longer
 * says "(coming soon)". Kept as a named flag so the label's test pins the state.
 */
export const MOBILE_CONTROLS_OVERLAY_BUILT = true;

const ORIENTATION_LABELS: Record<OrientationPref, string> = {
  auto: 'Auto (follow the device)',
  portrait: 'Portrait',
  landscape: 'Landscape',
};

/**
 * Display (q-portrait-hud-and-orientation-settings, AD-817; mobile-layout.md §4): the
 * Orientation lock and the Mobile-controls toggle. Orientation always holds the in-game
 * layout; the browser's own rotation is locked too only where it allows (fullscreen,
 * mostly Android Chrome) — silently skipped elsewhere, so the copy says "where supported".
 */
const BUS_LABEL: Record<AudioBus, string> = {
  master: 'Master',
  music: 'Music',
  sfx: 'Sound effects',
  voice: 'Voice',
};

/** AD-861: master / music / SFX / voice volume + mute, persisted (client-local). */
export function SoundSettings(): JSX.Element {
  const [settings, setSettings] = useState<AudioSettings>(getAudioSettings);
  useEffect(() => subscribeAudioSettings(setSettings), []);
  return (
    <>
      <SectionTitle>Sound</SectionTitle>
      {AUDIO_BUSES.map((bus) => (
        <label className="pxl-stat" key={bus}>
          <span className="k">
            {BUS_LABEL[bus]} ({Math.round(settings[bus].volume * 100)}%)
          </span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(settings[bus].volume * 100)}
            aria-label={`${BUS_LABEL[bus]} volume`}
            onChange={(e) => setBusSetting(bus, { volume: Number(e.target.value) / 100 })}
          />
          <input
            type="checkbox"
            checked={settings[bus].muted}
            aria-label={`Mute ${BUS_LABEL[bus]}`}
            onChange={(e) => setBusSetting(bus, { muted: e.target.checked })}
          />
          <span className="pxl-faint">mute</span>
        </label>
      ))}
    </>
  );
}

export function DisplaySettings(): JSX.Element {
  const prefs = useLayoutPrefs();
  return (
    <>
      <SectionTitle>Display</SectionTitle>
      <label className="pxl-stat">
        <span className="k">Screen orientation</span>
        <select
          className="pxl-select"
          value={prefs.orientation}
          data-testid="setting-orientation"
          onChange={(e) => {
            const orientation = parseOrientationPref(e.target.value);
            setLayoutPrefs({ orientation });
            // Inside the player's own gesture, so a fullscreen request (needed for the native
            // lock) is allowed. Never throws; the layout lock above already holds.
            void applyNativeOrientationLock(orientation, browserLockPorts(), true);
          }}
        >
          {ORIENTATION_PREFS.map((o) => (
            <option key={o} value={o}>
              {ORIENTATION_LABELS[o]}
            </option>
          ))}
        </select>
      </label>
      <label className="pxl-stat">
        <span className="k">
          On-screen controls{MOBILE_CONTROLS_OVERLAY_BUILT ? '' : ' (coming soon)'}
        </span>
        <input
          type="checkbox"
          checked={prefs.mobileControls}
          data-testid="setting-mobile-controls"
          onChange={(e) => setLayoutPrefs({ mobileControls: e.target.checked })}
        />
      </label>
      <div className="pxl-faint">
        Portrait or Landscape keeps the game&apos;s layout that way when you turn your device; the
        screen itself stays put only where your browser allows it (it may go full screen to do so).
        On-screen controls add a movement stick and touch buttons — tapping the ground to move and
        an enemy to attack works without them.
      </div>
    </>
  );
}

export function AccessibilityPanel(): JSX.Element {
  const [palette, setPalette] = useState<string>(
    () => localStorage.getItem('pxl.palette') ?? 'default',
  );
  const [reduceMotion, setReduceMotion] = usePersistentFlag('pxl.reduceMotion');
  const [highContrast, setHighContrast] = usePersistentFlag('pxl.highContrast');
  const [textScale, setTextScale] = usePersistentNumber('pxl.a11yScale', 100);

  // main.tsx applies the stored prefs at boot; this re-applies them as the player edits.
  useEffect(() => {
    localStorage.setItem('pxl.palette', palette);
    applyA11yPrefs(document.documentElement, { palette, reduceMotion, highContrast, textScale });
  }, [palette, reduceMotion, highContrast, textScale]);

  return (
    <div className="pxl-list">
      <SectionTitle>Vision</SectionTitle>
      <label className="pxl-stat">
        <span className="k">Colourblind palette</span>
        <select className="pxl-select" value={palette} onChange={(e) => setPalette(e.target.value)}>
          {['default', 'protanopia', 'deuteranopia', 'tritanopia', 'high-contrast'].map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </label>
      <label className="pxl-stat">
        <span className="k">Page text scale ({textScale}%)</span>
        <input
          type="range"
          min={80}
          max={160}
          value={textScale}
          onChange={(e) => setTextScale(Number(e.target.value))}
        />
      </label>
      <SectionTitle>Motion & contrast</SectionTitle>
      <label className="pxl-stat">
        <span className="k">Reduce motion</span>
        <input
          type="checkbox"
          checked={reduceMotion}
          onChange={(e) => setReduceMotion(e.target.checked)}
        />
      </label>
      <label className="pxl-stat">
        <span className="k">High-contrast telegraphs</span>
        <input
          type="checkbox"
          checked={highContrast}
          onChange={(e) => setHighContrast(e.target.checked)}
        />
      </label>
      <div className="pxl-faint">
        Settings persist locally. Telegraph high-contrast + reduced-motion hooks are exposed for the
        renderer to honour.
      </div>
    </div>
  );
}

export function DebugPanel(): JSX.Element {
  const { state, conn } = useGame();
  const [tab, setTab] = useState('conn');
  const [rawT, setRawT] = useState('inventoryRequest');
  const [rawD, setRawD] = useState('{}');
  const [rawErr, setRawErr] = useState('');
  const sendRaw = (): void => {
    try {
      const d = JSON.parse(rawD || '{}') as unknown;
      conn.send(rawT as ClientMessage['t'], d as never);
      setRawErr('sent');
    } catch (e) {
      setRawErr(String(e));
    }
  };

  return (
    <div>
      <Tabs
        tabs={[
          { id: 'conn', label: 'Connection' },
          { id: 'state', label: 'State' },
          { id: 'log', label: 'Logs' },
          { id: 'raw', label: 'Raw send' },
        ]}
        active={tab}
        onSelect={setTab}
      />
      {tab === 'conn' ? (
        <div className="pxl-list">
          <Stat k="Phase" v={conn.currentPhase} />
          <Stat k="RTT" v={`${Math.round(conn.rttMs)} ms`} />
          <Stat k="Server tick" v={state.serverTick} />
          <Stat k="Messages applied" v={state.messageCount} />
          <Stat k="Zone" v={state.ack?.zoneId ?? '—'} />
          <Stat k="Self entity" v={state.selfEntityId ?? '—'} />
          <Stat k="Entities" v={state.entities.size} />
          <Stat k="Buffs" v={state.buffs.length} />
        </div>
      ) : null}
      {tab === 'state' ? (
        <div className="pxl-list">
          <SectionTitle>Self / progression</SectionTitle>
          <DataView
            value={{
              wallet: state.wallet,
              stats: state.stats,
              level: state.level,
              stamina: state.stamina,
              loadout: state.loadout,
              masteries: state.masteries.length,
              reputation: state.reputation.length,
            }}
          />
          <SectionTitle>Inventory</SectionTitle>
          <DataView value={state.inventory} />
        </div>
      ) : null}
      {tab === 'log' ? (
        <div className="pxl-list">
          <SectionTitle>Combat ({state.combatLog.length})</SectionTitle>
          {state.combatLog.length === 0 ? <Empty>—</Empty> : null}
          {state.combatLog.slice(-30).map((l) => (
            <div key={l.id} className="pxl-faint">
              {l.text}
            </div>
          ))}
          <SectionTitle>Events ({state.eventLog.length})</SectionTitle>
          {state.eventLog.slice(-30).map((l) => (
            <div key={l.id} className="pxl-faint">
              {l.text}
            </div>
          ))}
        </div>
      ) : null}
      {tab === 'raw' ? (
        <div className="pxl-list">
          <SectionTitle>Send any client intent</SectionTitle>
          <input
            className="pxl-input"
            placeholder="message type (e.g. marketSearch)"
            value={rawT}
            onChange={(e) => setRawT(e.target.value)}
          />
          <textarea
            className="pxl-input"
            rows={4}
            placeholder='payload JSON e.g. {"contentId":"bog-iron"}'
            value={rawD}
            onChange={(e) => setRawD(e.target.value)}
          />
          <button className="pxl-btn primary" onClick={sendRaw}>
            send
          </button>
          {rawErr ? <div className="pxl-faint">{rawErr}</div> : null}
          <div className="pxl-faint">
            Developer tool (dev builds only). The server still validates every message.
          </div>
        </div>
      ) : null}
    </div>
  );
}
